import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { LEGACY_TENANT_SLUGS, Tenant, canonicalTenantSlug } from '../api/types';
import { paths } from '../routes';
import {
    tenantSelected,
    userAuthenticated,
} from '../action/authenticationEvents';
import {
    useLazyGetAccountPermissionsQuery,
    useLazyGetAccountQuery,
} from '../api/account/accountApiSlice';
import { toAuthPayload } from '../common/accountPermissions';
import { legacyTenantForPermission } from '../common/legacyTenantPermissions';
import {
    TenantService,
    getServicesForSolution,
    setStoredOrganizationName,
    setStoredServices,
    setStoredSolution,
} from '../common/selfServiceSolutions';
import LoadingPage from '../pages/common/LoadingPage';
import SignInRedirectPage from '../pages/common/SignInRedirectPage';
import { getTokenData } from '../auth0';
import { resolveInternalBootTenant } from '../utils/bootTenantResolution';
import { isDeadSessionError } from '../utils/authSession';
import { isTenantArchivedError } from '../utils/tenantArchived';

// First legacy tenant granted by the caller's JWT permissions. Resolution of a
// single permission is delegated to `legacyTenantForPermission` so the historic
// spellings (`tenant:vonovia-v2` for `vonovia_v2`, the misspelled
// `tenant:seeizeit` for `seezeit`) are recognized here exactly as they are in
// the TenantSwitcher — a grant the switcher honors must also resolve on boot,
// otherwise its holder is routed to /onboarding instead of into the tenant.
const pickLegacyTenantFromPermissions = (
    permissions: string[],
): Tenant | null => {
    for (const perm of permissions) {
        const slug = legacyTenantForPermission(perm);
        if (slug) return slug as Tenant;
    }
    return null;
};

/**
 * Resolves the current user's tenant on app boot, replacing the visible
 * "Please choose a tenant" screen with a transparent loading state.
 *
 * Resolution order:
 *   1. localStorage `selectedTenant` — only honored when the saved slug
 *      is a legacy tenant (Kolula-internal user who picked a tenant via
 *      the switcher). Any other value — stale self-service slug from a
 *      previous session, unknown slug, etc. — falls through to step 2
 *      so a fresh sign-up in the same browser still gets routed to
 *      /onboarding instead of being short-circuited into the previous
 *      user's tenant.
 *   2. JWT `tenant:<slug>` permissions — Kolula-internal admins whose
 *      auth0 JWT grants access to one or more legacy tenants. They must
 *      never be routed to the self-service onboarding screen even with
 *      no localStorage entry (fresh browser, cleared cache). Pick the
 *      first matching legacy slug from their permissions so the main
 *      layout mounts and the topbar TenantSwitcher takes over — EXCEPT
 *      when a MASTER's saved slug is a self-service tenant: only the
 *      master-gated switcher can have written it, so it is their own
 *      last selection and wins over the grant (dropping it snapped the
 *      X-Tenant header back to a legacy tenant on every reload and made
 *      the customers surface 409 — see bootTenantResolution.ts).
 *   3. GET /admin/account — for self-service users whose tenant lives in
 *      the operational DB. Uses the tenant's real `slug` as the Redux
 *      identifier so the X-Tenant header on subsequent requests matches
 *      the operational DB row directly. Loads the UI permission set from
 *      GET /admin/account/permissions (DB-driven via app_user.role) since
 *      self-service users have no `tenant:<slug>` permissions in their
 *      Auth0 JWT.
 *   4. Fallback: redirect to /onboarding.
 *
 * If the token itself cannot be fetched, there is no step to take, so this
 * never stays on the spinner: it shows the sign-in screen instead. This only
 * mounts after App.tsx's permissions effect has fetched the token once, so a
 * session that is dead from the start never reaches here — App.tsx redirects
 * and shows the sign-in screen itself. What lands here is a token that failed
 * in between: a dead session gets the screen with its delayed manual retry
 * (App.tsx owns the redirect; firing a second one here would overwrite its
 * PKCE transaction), any other failure the retry and sign-out at once.
 */
const TenantBootstrap = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [triggerGetAccount] = useLazyGetAccountQuery();
    const [triggerGetPermissions] = useLazyGetAccountPermissionsQuery();
    const ranRef = useRef(false);
    const [tokenFailure, setTokenFailure] = useState<'dead_session' | 'other' | null>(null);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        // Canonicalised: a value saved under the retired v1 spelling
        // (`vonovia` / `segmenta`) restores onto the current tenant instead of
        // one the switcher no longer lists — otherwise the user boots into a
        // tenant they cannot see or switch away from (CEO, 2026-09-18).
        const saved = localStorage.getItem('selectedTenant');
        if (saved && LEGACY_TENANT_SLUGS.has(saved)) {
            dispatch(tenantSelected({ tenant: canonicalTenantSlug(saved) as Tenant }));
            return;
        }

        const run = async () => {
            let tokenData: { permissions: string[], roles: string[] };
            try {
                tokenData = await getTokenData();
            } catch (err) {
                // Unhandled, this rejection left the tenant unresolved and the
                // user on an endless white spinner — what customers met after
                // their refresh token had expired (ADR-0023 amendment).
                setTokenFailure(isDeadSessionError(err) ? 'dead_session' : 'other');
                return;
            }
            const { permissions, roles } = tokenData;
            const legacySlug = pickLegacyTenantFromPermissions(permissions);
            if (legacySlug) {
                // Internal Kolula user with multi-tenant access. Mount the
                // main layout on their saved selection (masters may have
                // switched into a self-service tenant) or their first legacy
                // grant; the topbar TenantSwitcher handles further switching.
                const isMaster = (roles ?? []).some((r) => r.toLowerCase().includes('master'));
                dispatch(tenantSelected({
                    tenant: resolveInternalBootTenant(saved, legacySlug, isMaster) as Tenant,
                }));
                return;
            }

            triggerGetAccount()
                .unwrap()
                .then(async (res) => {
                    if ('onboardingPending' in res) {
                        navigate(paths.onboarding, { replace: true });
                        return;
                    }
                    if (!res.tenant.slug) {
                        // Tenant exists but slug isn't set. The backend
                        // routes by slug (no sentinel fallback), so we
                        // can't proceed — send the user back to
                        // onboarding to pick one. Idempotent register
                        // will return the existing tenant.
                        navigate(paths.onboarding, { replace: true });
                        return;
                    }
                    // Cache the tenant's solution + organization name before
                    // dispatching tenantSelected so App.tsx's config-synthesis
                    // effect sees the right services and brand text on first
                    // run.
                    setStoredSolution(res.tenant.solution);
                    // Prefer the tenant's stored config.services (source of
                    // truth); fall back to the solution map until it's set.
                    setStoredServices(
                        (res.tenant.services as TenantService[] | null)
                            ?? getServicesForSolution(res.tenant.solution),
                    );
                    setStoredOrganizationName(res.tenant.organizationName);
                    // Permissions come from our DB (app_user.role), served by
                    // GET /admin/account/permissions — the single source of
                    // truth, replacing the former hardcoded owner-grade set.
                    try {
                        const perms = await triggerGetPermissions().unwrap();
                        dispatch(userAuthenticated(toAuthPayload(perms)));
                    } catch {
                        // Transient failure: dispatch an empty grant so
                        // permissionsLoaded still flips and the app renders
                        // (self-service UI bypasses keep it usable); App.tsx's
                        // auth effect re-fetches. Better than a stuck spinner.
                        dispatch(userAuthenticated({ permissions: [], roles: [] }));
                    }
                    dispatch(tenantSelected({ tenant: res.tenant.slug as Tenant }));
                })
                .catch((err: unknown) => {
                    // #1379: a closed account is not an unfinished signup.
                    // The refusal has already raised the closed-account
                    // screen (store/tenantArchivedMiddleware); sending the
                    // owner to /onboarding would offer to register again.
                    if (isTenantArchivedError(err)) return;
                    navigate(paths.onboarding, { replace: true });
                });
        };

        run();
    }, [dispatch, navigate, triggerGetAccount]);

    if (tokenFailure) {
        return (
            <SignInRedirectPage
                error={tokenFailure === 'other' ? 'token_unavailable' : null}
            />
        );
    }

    return <LoadingPage />;
};

export default TenantBootstrap;
